import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import type { DataGridInstance, DataGridReactTable } from "../types";
import { type GridController, isFocusLost } from "./gridController";
import {
    findFocusableRowIndex,
    getFocusedAddress,
    getNavigableColumnIds,
    isCellFocusTarget,
    selectFocusedRowId,
} from "./navigation";
import { focusTableRow } from "./useAutoFocus";

/**
 * Keeps DOM focus in the grid while rows change under it. When a re-render
 * dropped focus (a reload remounted the rows), focus returns to the same row,
 * or to the same cell when a cell held it. When a new page or refetch
 * replaced the focused row, focus moves to the row at the same index, clamped
 * to the last row. Either way the focus visuals stay as shown or hidden as
 * they were.
 */
export const useKeepFocusInRows = <TData extends RowData>({
    instance,
    controller,
    getFocusedIndex,
}: {
    instance: DataGridInstance<TData>;
    controller: GridController;
    /** The focused row's index in the row model, or -1 when it's gone. */
    getFocusedIndex: () => number;
}) => {
    const lastIndexRef = useRef(0);
    const { cellSelection } = instance.table.atoms;

    useEffect(() => {
        const subscription = cellSelection.subscribe(() => {
            const index = getFocusedIndex();
            if (index >= 0) {
                lastIndexRef.current = index;
            }
        });
        return () => subscription.unsubscribe();
    }, [cellSelection, getFocusedIndex]);

    useLayoutEffect(() => {
        const { table, hasCellNavigation, rowKinds } = instance;
        controller.flushPendingFocus();
        const focused = getFocusedAddress(table.atoms.cellSelection.get());
        const focusedIndex = getFocusedIndex();
        if (focusedIndex >= 0) {
            lastIndexRef.current = focusedIndex;
        }
        const isCellFocused = isCellFocusTarget(instance);
        controller.restoreFocus(
            focused && {
                rowId: focused.rowId,
                columnId: isCellFocused ? focused.columnId : null,
            },
        );
        const rows = table.getRowModel().rows;
        if (
            focused &&
            focusedIndex < 0 &&
            rows.length > 0 &&
            controller.isFocusWithin.get() &&
            isFocusLost()
        ) {
            const index = findFocusableRowIndex({
                rows,
                isSeparator: rowKinds.isSeparator,
                rowIndex: lastIndexRef.current,
                fromIndex: lastIndexRef.current,
            });
            const row = rows[index];
            if (row) {
                const isColumnKept =
                    isCellFocused &&
                    (!hasCellNavigation ||
                        getNavigableColumnIds(table).includes(
                            focused.columnId,
                        ));
                const quiet = controller.getFocusQuiet();
                focusTableRow({
                    instance,
                    controller,
                    rowId: row.id,
                    columnId: isColumnKept ? focused.columnId : undefined,
                });
                controller.setFocusQuiet(quiet);
            }
        }
    });
};

/**
 * Whether the focused row is in the row model, so it holds tabindex 0; the
 * viewport is the tab stop only while it isn't.
 */
export const useIsFocusInRows = <TData extends RowData>(
    table: DataGridReactTable<TData>,
) => {
    const rows = table.getRowModel().rows;
    const rowIds = useMemo(() => new Set(rows.map(({ id }) => id)), [rows]);
    return useSelector(table.atoms.cellSelection, (ranges) => {
        const rowId = selectFocusedRowId(ranges);
        return rowId !== null && rowIds.has(rowId);
    });
};
